import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRow = ({ value, onChange, size = 'w-5 h-5', readOnly = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(i => {
        const active = (hover || value) >= i;
        return (
          <button
            key={i}
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && onChange && onChange(i)}
            onMouseEnter={() => !readOnly && setHover(i)}
            onMouseLeave={() => !readOnly && setHover(0)}
            className={`${readOnly ? 'cursor-default' : 'cursor-pointer'} transition-colors`}
            aria-label={`${i} star${i > 1 ? 's' : ''}`}
          >
            <Star className={`${size} ${active ? 'fill-[#c9a24a] text-[#c9a24a]' : 'text-[#c9c1ad]'}`} />
          </button>
        );
      })}
    </div>
  );
};

const Reviews = ({ productId }) => {
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', rating: 5, title: '', body: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reviews/${productId}`);
      setData(res.data);
    } catch (e) {
      console.error('Failed to load reviews', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.body.trim()) {
      toast.error('Please fill in your name and review.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { ...form, product_id: productId });
      toast.success('Thank you for your review!');
      setForm({ name: '', rating: 5, title: '', body: '' });
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error('Could not submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-16">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[#e5decf] pb-4 mb-8">
        <div>
          <div className="uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-2">Reviews</div>
          <h2 className="font-serif-elegant text-3xl md:text-4xl">What people are saying</h2>
        </div>
        <div className="flex items-center gap-4">
          {data.count > 0 && (
            <div className="flex items-center gap-2">
              <StarRow value={Math.round(data.average)} readOnly size="w-4 h-4" />
              <span className="text-sm text-[#4a4640]">{data.average.toFixed(1)} &middot; {data.count} review{data.count > 1 ? 's' : ''}</span>
            </div>
          )}
          <button onClick={() => setShowForm(!showForm)} className="btn-outline">
            {showForm ? 'Cancel' : 'Write a Review'}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-[#efe9db] p-6 mb-10 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-2 block">Your name</label>
              <input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full bg-[#faf7f1] border border-[#c9c1ad] px-3 py-2 text-sm focus:outline-none focus:border-[#3a4a3b]"
                required
              />
            </div>
            <div>
              <label className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-2 block">Rating</label>
              <StarRow value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
            </div>
          </div>
          <div>
            <label className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-2 block">Title (optional)</label>
            <input
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full bg-[#faf7f1] border border-[#c9c1ad] px-3 py-2 text-sm focus:outline-none focus:border-[#3a4a3b]"
            />
          </div>
          <div>
            <label className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-2 block">Your review</label>
            <textarea
              rows="4"
              value={form.body}
              onChange={e => setForm({ ...form, body: e.target.value })}
              className="w-full bg-[#faf7f1] border border-[#c9c1ad] px-3 py-2 text-sm focus:outline-none focus:border-[#3a4a3b] resize-none"
              required
            />
          </div>
          <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60">
            {submitting ? 'Submitting...' : 'Post Review'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-[#6f6a5f]">Loading reviews...</div>
      ) : data.reviews.length === 0 ? (
        <div className="py-12 text-center text-[#6f6a5f] bg-[#efe9db]">
          <p className="font-serif-elegant text-2xl mb-2">Be the first to share.</p>
          <p className="text-sm">Your kind words help others find their remedy.</p>
        </div>
      ) : (
        <ul className="space-y-8">
          {data.reviews.map(r => (
            <li key={r.id} className="pb-8 border-b border-[#e5decf] last:border-b-0">
              <div className="flex items-center gap-3 mb-2">
                <StarRow value={r.rating} readOnly size="w-4 h-4" />
                <span className="text-xs uppercase tracking-widest text-[#6f6a5f]">
                  {new Date(r.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                </span>
              </div>
              {r.title && <h4 className="font-serif-elegant text-xl mb-1">{r.title}</h4>}
              <p className="text-[#4a4640] leading-relaxed mb-2">{r.body}</p>
              <div className="text-sm text-[#7a8b6b]">&mdash; {r.name}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default Reviews;
export { StarRow };
